/**
 * useNotes
 *
 * Fetches aggregated notes (space_notes + message-based) from /api/notes/list
 * and provides refetch. Used by Losi Notes page.
 *
 * Pass `{ pageSize }` to page the unified list (scroll-to-bottom append with
 * id dedupe); without it the historical full-list fetch is used unchanged.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { UnifiedNoteItem } from '@/app/api/notes/list/route'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'

interface NotesPageMeta {
  limit: number
  offset: number
  count: number
  hasMore: boolean
}

interface UseNotesResult {
  notes: UnifiedNoteItem[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  updateNoteLocally: (
    noteId: string,
    updates: Partial<Pick<UnifiedNoteItem, 'title' | 'body' | 'updated_at'>>
  ) => void
  /** Paged mode only: more pages exist beyond the loaded window. */
  hasMore: boolean
  /** Paged mode only: a load-more fetch is in flight. */
  loadingMore: boolean
  /** Paged mode only: append the next page (no-op when exhausted). */
  loadMore: () => Promise<void>
}

export function useNotes(options?: { pageSize?: number }): UseNotesResult {
  const pageSize =
    options?.pageSize && options.pageSize > 0
      ? Math.floor(options.pageSize)
      : undefined
  const [notes, setNotes] = useState<UnifiedNoteItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  // Offset of the next page to fetch (paged mode) + race guards that must not
  // retrigger renders.
  const nextOffsetRef = useRef(0)
  const hasMoreRef = useRef(false)
  const loadingMoreRef = useRef(false)

  const applyPageMeta = useCallback(
    (page: NotesPageMeta | undefined, items: UnifiedNoteItem[]) => {
      nextOffsetRef.current = (page?.offset ?? 0) + (page?.count ?? items.length)
      const more = Boolean(page?.hasMore)
      hasMoreRef.current = more
      setHasMore(more)
    },
    []
  )

  const fetchNotes = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (!pageSize) {
        const res = await authenticatedFetch('/api/notes/list')
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.error ?? res.statusText)
        }
        const data = await res.json()
        setNotes(data.unified ?? [])
        return
      }
      // Refetch keeps the loaded depth so saving a note doesn't collapse the
      // list back to page 1.
      const limit = Math.max(pageSize, nextOffsetRef.current)
      const res = await authenticatedFetch(
        `/api/notes/list?limit=${limit}&offset=0`
      )
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error ?? res.statusText)
      }
      const data = await res.json()
      const items: UnifiedNoteItem[] = data.unified ?? []
      setNotes(items)
      applyPageMeta(data.page as NotesPageMeta | undefined, items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load notes')
      setNotes([])
    } finally {
      setLoading(false)
    }
  }, [pageSize, applyPageMeta])

  const loadMore = useCallback(async () => {
    if (!pageSize || loadingMoreRef.current || !hasMoreRef.current) return
    loadingMoreRef.current = true
    setLoadingMore(true)
    try {
      const res = await authenticatedFetch(
        `/api/notes/list?limit=${pageSize}&offset=${nextOffsetRef.current}`
      )
      if (!res.ok) return
      const data = await res.json()
      const items: UnifiedNoteItem[] = data.unified ?? []
      // Pages can overlap when a note is edited mid-scroll (updated_at bumps
      // it into an earlier window) — dedupe by id on append.
      setNotes((current) => {
        const seen = new Set(current.map((n) => n.id))
        return [...current, ...items.filter((i) => !seen.has(i.id))]
      })
      applyPageMeta(data.page as NotesPageMeta | undefined, items)
    } catch {
      // Keep what's loaded; the sentinel retries on the next scroll pass.
    } finally {
      loadingMoreRef.current = false
      setLoadingMore(false)
    }
  }, [pageSize, applyPageMeta])

  useEffect(() => {
    fetchNotes()
  }, [fetchNotes])

  const updateNoteLocally = useCallback(
    (
      noteId: string,
      updates: Partial<Pick<UnifiedNoteItem, 'title' | 'body' | 'updated_at'>>
    ) => {
      setNotes((currentNotes) =>
        [...currentNotes]
          .map((note) =>
            note.id === noteId
              ? {
                  ...note,
                  ...updates
                }
              : note
          )
          .sort((leftNote, rightNote) => {
            const leftTime = new Date(leftNote.updated_at).getTime()
            const rightTime = new Date(rightNote.updated_at).getTime()
            return rightTime - leftTime
          })
      )
    },
    []
  )

  return {
    notes,
    loading,
    error,
    refetch: fetchNotes,
    updateNoteLocally,
    hasMore,
    loadingMore,
    loadMore
  }
}
